/*
 *   File : reject-mfd-modal.jsx
 *   Author : https://evoqins.com
 *   Description : reject MFD application modal component
 *   Integrations : NA
 *   Version : 1.0.0
 */

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PrimaryButton } from "../Buttons";
import { CheckboxSelect, CustomTextArea } from "../FormElements";
import { APIService } from "../../Service";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

const REJECTION_REASON_OPTIONS = [
  { value: "invalid_arn", label: "Invalid or unclear ARN document" },
  { value: "pan_mismatch", label: "PAN details mismatch" },
  { value: "bank_mismatch", label: "Bank account verification failed" },
  { value: "incomplete_docs", label: "Incomplete documentation" },
  { value: "other", label: "Other" },
];

const ModalOverlay = ({ onClose }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
      style={{ zIndex: 1040 }}
      onClick={onClose}
    />
  );
};

export default function RejectMfdModal({
  open = false,
  onCancel,
  onConfirm
}) {

  const { id } = useParams();

  const [reason, setReason] = useState(null);
  const [comments, setComments] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [commentsError, setCommentsError] = useState("");

  const [rejectedReason, setRejectedReason] = useState([]);
  const[rejectLoading, setRejectLoading] = useState(false);

  useEffect(() => {
    if (open) {
      _getRejectedReason();
    }
  }, [open]);


  useEffect(() => {
    if (!open) {
      setReason(null);
      setComments("");
      setReasonError("");
      setCommentsError("");
    }
  }, [open]);

  const validate = () => {
    let valid = true;

    if (!reason) {
      setReasonError("Reason for rejection is required");
      valid = false;
    } else {
      setReasonError("");
    }

    if (!comments.trim()) {
      setCommentsError("Additional comments are required");
      valid = false;
    } else {
      setCommentsError("");
    }

    return valid;
  };

  const handleReject = () => {
    if (!validate()) return;
    
    _rejectMfd();
  };


   // API - Get Rejected Reason
   const _getRejectedReason = () => {
    let is_post = false;
    let url = "partner/rejection-reasons";

    APIService(is_post, url)
      .then((response) => {
        if (response.status_code === 200) {
          setRejectedReason(response.data);
        } else {
          setRejectedReason([]);
        }
      })
      .catch((error) => {
        setRejectedReason([]);
      })
  };
  
  // API - Reject MFD Application
  const _rejectMfd= () => {
    setRejectLoading(true);
    let is_post = true;
    let url = "partner/reject";
    let data = {
      id: Number(id),
      reason_id: reason?.value,
      message: comments,
    };

    APIService(is_post, url, data)
      .then((response) => {
        if (response.status_code === 200) {
          onConfirm?.();
        } else {
          toast.dismiss();
          toast.error(response.message || "Failed to reject", {
            type: "error",
            className: "e-toast",
          });
        }
      })
      .catch((error) => {
        toast.dismiss();
        toast.error("Failed to reject", {
          type: "error",
          className: "e-toast",
        });
      })
      .finally(() => {
        setRejectLoading(false);
      });
  };

  if (!open) return null;

  return createPortal(
    <>
      <ModalOverlay onClose={onCancel} />

      <div
        className="position-fixed overflow-hidden top-50 start-50 translate-middle e-modal-portal rounded-28 shadow p-0"
        style={{
          zIndex: 1055,
          width: "600px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex e-bg-blur-20 flex-column row-gap-3 justify-content-start bg-white bg-opacity-80 align-items-start p-24">
          <div className="d-flex justify-content-between align-items-center w-100">
            <h5 className="fw-500 fs-18 lh-24 text-eerie-black mb-0">
              Reject MFD application
            </h5>
            <PrimaryButton
              icon="close"
              size={16}
              ClassName="bg-transparent p-0"
              onPress={onCancel}
            />
          </div>

          <div className="w-100">
            <p className="fw-400 fs-16 lh-24 text-eerie-black-2 mb-0">
              Please provide a reason for rejecting this application.
            </p>
            <p className="fw-400 fs-16 lh-24 text-eerie-black-2 mb-0">
              This will be shared with the user to help them correct the details
            </p>
          </div>

          <div className="d-flex flex-column w-100 pt-1">
            <p className="mb-0 fw-400 fs-14 pb-2 lh-20 text-eerie-black">
              Reason for rejection <span className="text-cg-red">*</span>
            </p>
            <CheckboxSelect
              options={rejectedReason}
              value={reason}
              onChange={(val) => {
                setReason(val);
                setReasonError("");
              }}
              placeholder="Select reason"
              error={reasonError}
            />
          </div>

          <div className="d-flex flex-column w-100">
            <CustomTextArea
              label="Additional comments"
              require
              labelStyle="fw-400 fs-14 lh-20 text-eerie-black mb-0"
              placeholder="Enter comments"
              value={comments}
              handleChange={(val) => {
                setComments(val);
                setCommentsError("");
              }}
              error={commentsError}
              rows={4}
              className="w-100 px-3 py-10"
            />
          </div>

          <div className="row w-100 pt-2">
            <div className="col-6">
              <PrimaryButton
                name="Cancel"
                ClassName="rounded-pill bg-transparent border-oxford-blue lh-24 py-3 text-oxford-blue w-100 fs-16 fw-500"
                onPress={onCancel}
              />
            </div>
            <div className="col-6">
              <PrimaryButton
                name={rejectLoading ? "Rejecting..." : "Reject"}
                disabled={rejectLoading}
                loading={rejectLoading}
                ClassName="rounded-pill bg-oxford-blue lh-24 py-3 text-white w-100 fs-16 fw-500"
                onPress={handleReject}
              />
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
